import React, { useEffect, useState } from "react";
import api from "../../../api/axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Save, ArrowLeft } from "lucide-react";

function CreateActive() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [areas, setAreas] = useState([]);
  const [isSavingAndContinuing, setIsSavingAndContinuing] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    model: "",
    serial_number: "",
    brand: "",
    quantity: 1,
    state: "",
    observation: "",
    active_category_id: "",
    active_location_id: "",
    active_area_id: "",
  });

  useEffect(() => {
    fetchCategories();
    fetchLocations();
    fetchAreas();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await api.get("/active-categories");
      setCategories(Array.isArray(res.data) ? res.data : res.data.categories || []);
    } catch {
      toast.error("Error al cargar categorías");
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await api.get("/active-locations");
      setLocations(Array.isArray(res.data) ? res.data : res.data.locations || []);
    } catch {
      toast.error("Error al cargar ubicaciones");
    }
  };

  const fetchAreas = async () => {
    try {
      const res = await api.get("/active-areas");
      setAreas(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Error al cargar áreas");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" })); // limpiar error al escribir
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.model.trim())
      newErrors.model = "El modelo es obligatorio";

    if (!formData.quantity || formData.quantity < 1)
      newErrors.quantity = "La cantidad debe ser mayor a 0";

    if (!formData.state)
      newErrors.state = "Debe seleccionar un estado";

    if (!formData.active_category_id)
      newErrors.active_category_id = "Debe seleccionar una categoría";

    if (!formData.active_location_id)
      newErrors.active_location_id = "Debe seleccionar una ubicación";

    if (!formData.active_area_id)
      newErrors.active_area_id = "Debe seleccionar un área";

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      await api.post("/actives", formData);
      toast.success("Activo creado exitosamente");
      setErrors({});

      if (isSavingAndContinuing) {
        setFormData({
          model: "",
          serial_number: "",
          brand: "",
          quantity: 1,
          state: "",
          observation: "",
          active_category_id: "",
          active_location_id: "",
          active_area_id: "",
        });
        setIsSavingAndContinuing(false);
      } else {
        navigate("/actives");
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        let backendErrors = {};
        error.response.data.errors.forEach((err) => {
          backendErrors[err.path] = err.msg;
        });
        setErrors(backendErrors);
      } else {
        toast.error("Error al crear el activo");
      }
    }
  };

  const inputClass = (field) =>
    `w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${errors[field]
      ? "border-red-500 focus:ring-red-500"
      : "border-gray-300 focus:ring-blue-500"
    }`;

  return (
    <div className="bg-white p-6 rounded-xl shadow-md max-w-xl mx-auto">
      <button
        onClick={() => navigate("/actives")}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} /> Volver a activos
      </button>

      <h2 className="text-3xl font-semibold text-gray-800 mb-6">
        Crear Activo
      </h2>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* MODELO */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
          <input
            name="model"
            value={formData.model}
            onChange={handleChange}
            className={inputClass("model")}
          />
          {errors.model && <p className="text-red-500 text-sm">{errors.model}</p>}
        </div>

        {/* SERIAL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Número de Serie</label>
          <input
            name="serial_number"
            value={formData.serial_number}
            onChange={handleChange}
            className={inputClass("serial_number")}
          />
          {errors.serial_number && (
            <p className="text-red-500 text-sm">{errors.serial_number}</p>
          )}
        </div>

        {/* MARCA / CANTIDAD */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
            <input
              name="brand"
              value={formData.brand}
              onChange={handleChange}
              className={inputClass("brand")}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad</label>
            <input
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              className={inputClass("quantity")}
            />
            {errors.quantity && (
              <p className="text-red-500 text-sm">{errors.quantity}</p>
            )}
          </div>
        </div>

        {/* ESTADO */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
          <select
            name="state"
            value={formData.state}
            onChange={handleChange}
            className={inputClass("state")}
          >
            <option value="">Seleccionar estado</option>
            <option value="En Uso">En uso</option>
            <option value="Disponible">Disponible</option>
            <option value="Reparación">Reparación</option>
          </select>
          {errors.state && <p className="text-red-500 text-sm">{errors.state}</p>}
        </div>

        {/* OBSERVACIÓN */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Observación</label>
          <textarea
            name="observation"
            value={formData.observation}
            onChange={handleChange}
            className={inputClass("observation")}
          />
        </div>

        {/* CATEGORIA / UBICACIÓN */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
            <select
              name="active_category_id"
              value={formData.active_category_id}
              onChange={handleChange}
              className={inputClass("active_category_id")}
            >
              <option value="">Seleccionar</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {errors.active_category_id && (
              <p className="text-red-500 text-sm">{errors.active_category_id}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ubicación</label>
            <select
              name="active_location_id"
              value={formData.active_location_id}
              onChange={handleChange}
              className={inputClass("active_location_id")}
            >
              <option value="">Seleccionar</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
            {errors.active_location_id && (
              <p className="text-red-500 text-sm">{errors.active_location_id}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Área
            </label>

            <select
              name="active_area_id"
              value={formData.active_area_id}
              onChange={handleChange}
              className={inputClass("active_area_id")}
            >
              <option value="">Seleccionar</option>

              {areas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </select>

            {errors.active_area_id && (
              <p className="text-red-500 text-sm">
                {errors.active_area_id}
              </p>
            )}
          </div>
        </div>

        {/* BOTONES */}
        <div className="flex gap-4">
          <button
            type="submit"
            onClick={() => setIsSavingAndContinuing(false)}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg shadow"
          >
            <Save size={18} /> Crear
          </button>

          <button
            type="submit"
            onClick={() => setIsSavingAndContinuing(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg shadow"
          >
            <Save size={18} /> Crear y seguir
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateActive;
